"use client";

import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { formatDuration } from "@offstreetparking/design-system/formatters";
import { DesignSystemI18nProvider } from "@offstreetparking/design-system/providers";
import { createContext, useContext, useMemo } from "react";
import * as z from "zod/v4";

const ZodContext = createContext<typeof z>(z);

export const useZod = () => {
	const context = useContext(ZodContext);
	if (!context) {
		throw new Error("useZod must be used within a TranslationsProvider");
	}
	return context;
};
type TranslationsProviderProps = {
	children: React.ReactNode;
};
export const TranslationsProvider = ({ children }: TranslationsProviderProps) => {
	const { i18n: linguiI18n } = useLingui();

	const designSystemI18n = useMemo(
		() => ({
			required: linguiI18n._(msg`required`),
			autocomplete: {
				placeholder: linguiI18n._(msg`Search...`),
			},
			button: {
				showLess: linguiI18n._(msg`Show less`),
				showMore: (count: number) =>
					count === 0 ? linguiI18n._(msg`Show more`) : linguiI18n._(msg`+ ${count} more`),
				clear: linguiI18n._(msg`Clear`),
			},
			timeInput: {
				openTimePicker: linguiI18n._(msg`Open time picker`),
				cancelButton: linguiI18n._(msg`Cancel`),
				applyButton: linguiI18n._(msg`Apply`),
			},
			daysOfWeek: {
				monday: linguiI18n._(msg`Monday`),
				tuesday: linguiI18n._(msg`Tuesday`),
				wednesday: linguiI18n._(msg`Wednesday`),
				thursday: linguiI18n._(msg`Thursday`),
				friday: linguiI18n._(msg`Friday`),
				saturday: linguiI18n._(msg`Saturday`),
				sunday: linguiI18n._(msg`Sunday`),
			},
			daysOfWeekShort: {
				monday: linguiI18n._(msg`Mon`),
				tuesday: linguiI18n._(msg`Tue`),
				wednesday: linguiI18n._(msg`Wed`),
				thursday: linguiI18n._(msg`Thu`),
				friday: linguiI18n._(msg`Fri`),
				saturday: linguiI18n._(msg`Sat`),
				sunday: linguiI18n._(msg`Sun`),
			},
			parkingOption: {
				startDateWithNoExpiry: linguiI18n._({
					...msg({ message: "Starting {startDate} with no expiry" }),
					values: { startDate: "" },
				}),
				startsNow: linguiI18n._(msg`Starts Now`),
				noExpiry: linguiI18n._(msg`Never Expires`),
				viewMore: (count: number) => linguiI18n._(msg`View ${count} more`),
				everyDay: linguiI18n._(msg`Daily`),
				allDay: linguiI18n._(msg`All day`),
				whenRegistered: linguiI18n._(msg`When Registered`),
				userSelects: linguiI18n._(msg`User Selects`),
				afterDuration: linguiI18n._(msg`After Duration`),
				duration: (duration: {
					minutes: number;
					hours: number;
					days: number;
					weeks: number;
				}) => formatDuration({ duration, i18n: linguiI18n }),
				afterDays: (count: number) =>
					count === 0
						? linguiI18n._(msg`Same Day`)
						: count === 1
							? linguiI18n._(msg`After ${count} Day`)
							: linguiI18n._(msg`After ${count} Days`),
				sameDay: linguiI18n._(msg`Same Day`),
				specificDays: linguiI18n._(msg`Specific Days`),
				editDatesAndTimes: linguiI18n._(msg`Edit dates and times`),
			},
			show: (field: string) => linguiI18n._(msg`Show ${field}`),
			hide: (field: string) => linguiI18n._(msg`Hide ${field}`),
		}),
		[linguiI18n],
	);
	return (
		<DesignSystemI18nProvider value={designSystemI18n}>
			<ZodContext.Provider value={z}>{children}</ZodContext.Provider>
		</DesignSystemI18nProvider>
	);
};
